import type {
  CustomerConfig,
  CustomerDomain,
  CustomerSettings,
} from "@/redux/reducer/customers.api";
import {
  consentCoreHTML,
  cookiebotHeadHTML,
  isEmbeddedInIframe,
  ontameHeadHTML,
} from "./pageDocument";

export type HeadReplacement = { old: string; new: string };
export type FrameHeadResult = {
  status: "updated" | "unchanged" | "skipped";
  reason: string | null;
};
type FramePageType = "page" | "job";
export type FramePageResult = FrameHeadResult & {
  id: string;
  type: FramePageType;
  name: string;
};

// The head parts the iframe switch changes, exactly as the editor's save writes them, from the other setting to this one.
export const frameHeadReplacements = ({
  settings,
  tenantId,
  isJobPage,
  embeddedInIframe,
}: {
  settings: CustomerSettings | undefined;
  tenantId: string;
  isJobPage: boolean;
  embeddedInIframe: boolean;
}): HeadReplacement[] => {
  const parts = (embedded: boolean) => [
    consentCoreHTML(embedded),
    cookiebotHeadHTML({ settings, embeddedInIframe: embedded }),
    ontameHeadHTML({ settings, tenantId, isJobPage, embeddedInIframe: embedded }).join(""),
  ];
  const before = parts(!embeddedInIframe);
  const after = parts(embeddedInIframe);
  return before
    .map((old, i) => ({ old, new: after[i] }))
    .filter((part) => part.old !== part.new);
};

const pagesOf = (domain: CustomerDomain | undefined) =>
  domain?.joblist_page
    ? [
        domain.joblist_page.joblist_page_id,
        ...domain.joblist_page.job_ad_pages.map((page) => page.job_ad_page_id),
      ]
    : [];

// Brings every page the domain holds to the setting its domains call for. A failed request stops the run.
export async function applyFrameSetting({
  domainId,
  domains,
  templates,
  settings,
  tenantId,
  applyHead,
}: {
  domainId: string;
  domains: CustomerDomain[];
  templates: { pages: { id: string; name: string }[]; ads: { id: string; name: string }[] };
  settings: CustomerSettings | undefined;
  tenantId: string;
  applyHead: (request: {
    type: FramePageType;
    id: string;
    replacements: HeadReplacement[];
  }) => Promise<FrameHeadResult>;
}): Promise<FramePageResult[]> {
  const results: FramePageResult[] = [];
  for (const id of pagesOf(domains.find((domain) => domain.domain_id === domainId))) {
    const page = templates.pages.find((template) => template.id === id);
    const ad = templates.ads.find((template) => template.id === id);
    const template = page ?? ad;
    if (!template) {
      continue;
    }
    const type: FramePageType = page ? "page" : "job";
    const replacements = frameHeadReplacements({
      settings,
      tenantId,
      isJobPage: type === "job",
      embeddedInIframe: isEmbeddedInIframe(domains, id),
    });
    const result = await applyHead({ type, id, replacements });
    results.push({ id, type, name: template.name, ...result });
  }
  return results;
}

// The config a save writes into the page; without one the page would lose its Cookiebot and Ontame wiring.
export async function configAtSave({
  fetchFresh,
  lastKnown,
}: {
  fetchFresh: () => Promise<Pick<CustomerConfig, "domains" | "settings">>;
  lastKnown: Pick<CustomerConfig, "domains" | "settings"> | undefined;
}) {
  try {
    return await fetchFresh();
  } catch (error) {
    if (lastKnown) {
      return lastKnown;
    }
    throw error;
  }
}

export async function saveThenPublish<T>({
  save,
  publish,
}: {
  save: () => Promise<unknown>;
  publish: () => Promise<T>;
}): Promise<T> {
  await save();
  return publish();
}
